/**
 * Connect - Sistema de Juegos brand identity.
 * Theme-aware raster assets derived deterministically from the official brand
 * manual logo (transparent background, teal infinity mark preserved):
 *
 *   • dark variant  → white wordmark  (use on navy / dark surfaces)
 *   • light variant → navy wordmark   (use on white / light surfaces)
 *
 *   ConnectLogoImage — full logo with "SISTEMA DE JUEGOS" tagline (hero/login).
 *   ConnectLogo      — compact wordmark, no tagline (shells / top bars).
 *
 * `variant` prop:  "auto" (default, follows active theme) | "dark" | "light".
 */
import { useTheme } from "@/lib/theme-context";

const FULL = {
  dark: "/brand/connect-logo-dark.png",
  light: "/brand/connect-logo-light.png",
};
const COMPACT = {
  dark: "/brand/connect-logo-compact-dark.png",
  light: "/brand/connect-logo-compact-light.png",
};

function resolve(set, variant, theme) {
  // "dark" asset = white wordmark, shown on dark surfaces / dark theme.
  if (variant === "dark") return set.dark;
  if (variant === "light") return set.light;
  return theme === "light" ? set.light : set.dark;
}

/** Full official logo (includes tagline). */
export function ConnectLogoImage({ className = "", variant = "auto", alt = "Connect - Sistema de Juegos" }) {
  const { theme } = useTheme();
  return (
    <img
      src={resolve(FULL, variant, theme)}
      alt={alt}
      className={`block mx-auto ${className}`}
      draggable={false}
    />
  );
}

/** Compact interior logo (mark + wordmark, no tagline). Sized via `height` (px). */
export function ConnectLogo({ height = 32, className = "", variant = "auto", alt = "Connect" }) {
  const { theme } = useTheme();
  return (
    <img
      src={resolve(COMPACT, variant, theme)}
      alt={alt}
      style={{ height: `${height}px`, width: "auto" }}
      className={`inline-block select-none ${className}`}
      draggable={false}
    />
  );
}

export default ConnectLogo;
